import { StreetOrientationMode } from "building-types/building/streetOrientationMode";
import { BuiltInExteriorPropId } from "building-types/props/builtInExteriorPropId";
import { PropId } from "building-types/props/propIds";
import type { ExteriorPropSettings } from "building-types/settings/exteriorPropSettings";
import type { HomeSettings } from "building-types/settings/homeSettings";
import type { PorchSettings } from "building-types/settings/porchSettings";
import { SettingsType } from "building-types/settings/settingsType";
import { AwningType, EaveType, FasciaTopTrimSize, HeadCasingType } from "building-types/settings/settingsTypesEnum";
import { defaultExteriorPropCatalog } from "../elements/propCatalog/exteriorPropCatalog";
import { fasciaTopTrimSizeToValue } from "../fasciaTopTrimSizeToValue";
import { BuiltinHomeSettingsId, builtinHomeSettingsPrettyName } from "./builtinHomeSettingsId";
import {
  defaultAwningSettings,
  defaultExteriorDoorCasingSettings,
  defaultFarmhouseWindowSettings,
  defaultFriezeBoardSettings,
  defaultHomeSettings,
  defaultPorchSettings,
  defaultWainscotingSettings,
} from "./defaultSettings";

const epconPorchSettings: PorchSettings = {
  ...defaultPorchSettings,
  maxColumnSpacing: 120,
  doorPathColumnSpread: 60,
  columnId: undefined,
  includeRailing: true,
};

const epconExteriorPropSettings: ExteriorPropSettings = {
  type: SettingsType.EXTERIOR_PROP_SETTINGS,
  propTypes: {
    ...defaultExteriorPropCatalog,
    garageDoorSingle: {
      ...defaultExteriorPropCatalog[BuiltInExteriorPropId.GARAGEDOORSINGLE],
      propAssetId: PropId.DoorGar_S8x7_2x3x2Pattern_4Lite4th_01,
    },
    garageDoorDouble: {
      ...defaultExteriorPropCatalog[BuiltInExteriorPropId.GARAGEDOORDOUBLE],
      propAssetId: PropId.DoorGar_D16x7_2x3x2Pattern_4Lite4th_01,
    },
    doorSingleGlass: {
      ...defaultExteriorPropCatalog[BuiltInExteriorPropId.DOORSINGLEGLASS],
      propAssetId: PropId.DoorExt_Single36_QuarterSolid_02,
    },
    doorSingleGlassLow: {
      ...defaultExteriorPropCatalog[BuiltInExteriorPropId.DOORSINGLEGLASSLOW],
      propAssetId: PropId.DoorExt_Single36_QuarterSolid_01,
    },
  },
};

const epconSettings: { [id: string]: HomeSettings } = {
  [BuiltinHomeSettingsId.Epcon_P2C]: {
    ...defaultHomeSettings,
    name: builtinHomeSettingsPrettyName[BuiltinHomeSettingsId.Epcon_P2C],
    exteriorMaterials: {
      id: BuiltinHomeSettingsId.Epcon_P2C + "_exterior",
      type: SettingsType.EXTERIOR_MATERIAL_SETTINGS,
      palettes: [BuiltinHomeSettingsId.Epcon_P2C],
    },
    exteriorProps: epconExteriorPropSettings,
    roof: {
      type: SettingsType.ROOF_SETTINGS,
      eaveOverhang: 14,
      rakeOverhang: 14,
      shedOverhang: 8,
      eaveType: EaveType.ANGLED_ENCLOSED_OPEN,
      includeFasciaTopTrim: true,
      fasciaTopTrimSize: fasciaTopTrimSizeToValue[FasciaTopTrimSize.TWO_INCH],
      eaveFasciaSize: 8,
      rakeFasciaSize: 8,
      rakeFriezeBoard: defaultFriezeBoardSettings,
      includeFriezeBoardOnPorches: true,
    },
    wainscoting: {
      ...defaultWainscotingSettings,
      height: 0,
      wainscotingMode: StreetOrientationMode.FRONT_FACES,
      closeDist: 0,
    },
    ...defaultFarmhouseWindowSettings,
    awning: { ...defaultAwningSettings, type: SettingsType.AWNING_SETTINGS, awningType: AwningType.DISABLED },
    porch: epconPorchSettings,
    exteriorDoorCasing: {
      ...defaultExteriorDoorCasingSettings,
      headCasingType: HeadCasingType.STONE,
      headCasingHeight: 6,
      headCasingDepth: 2,
    },
  },
};

export default epconSettings;
